import React, { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
	Elements,
	CardElement,
	useStripe,
	useElements,
} from "@stripe/react-stripe-js";
import { STRIPE_CONFIG } from "../config/stripeConfig";
import { createStripeSubscription } from "../services/authService";

// Initialize Stripe
const stripePromise = loadStripe(STRIPE_CONFIG.PUBLISHABLE_KEY);

// Card Element styling
const CARD_ELEMENT_OPTIONS = {
	style: {
		base: {
			color: "#ffffff",
			fontFamily: '"Candara", "Helvetica Neue", Helvetica, sans-serif',
			fontSmoothing: "antialiased",
			fontSize: "16px",
			"::placeholder": {
				color: "#aab7c4",
			},
		},
		invalid: {
			color: "#fa755a",
			iconColor: "#fa755a",
		},
	},
};

// Payment Form Component (inside Elements provider)
function PaymentForm({ selectedPlan, onSuccess, onClose }) {
	const stripe = useStripe();
	const elements = useElements();
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [cardName, setCardName] = useState("");

	// Legal consent checkboxes
	const [acceptedCGU, setAcceptedCGU] = useState(false);
	const [acceptedPrivacy, setAcceptedPrivacy] = useState(false);
	const [acceptedAutoRenewal, setAcceptedAutoRenewal] = useState(false);
	const [acceptedWithdrawalWaiver, setAcceptedWithdrawalWaiver] = useState(false);

	const isFormValid = () => {
		return (
			cardName.length > 0 &&
			acceptedCGU &&
			acceptedPrivacy &&
			acceptedAutoRenewal &&
			acceptedWithdrawalWaiver
		);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!stripe || !elements || loading) {
			return;
		}

		if (!isFormValid()) {
			setError("Veuillez remplir tous les champs et accepter les conditions");
			return;
		}

		setLoading(true);
		setError(null);

		try {
			// Step 1: Create Subscription on backend
			const subscriptionResult = await createStripeSubscription(
				selectedPlan.idAbonnementType
			);

			if (!subscriptionResult.success || !subscriptionResult.clientSecret) {
				throw new Error(subscriptionResult.error || "Erreur lors de la création de l'abonnement");
			}

			const { clientSecret } = subscriptionResult;

			// Step 2: Confirm payment with Stripe
			const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(
				clientSecret,
				{
					payment_method: {
						card: elements.getElement(CardElement),
						billing_details: {
							name: cardName,
						},
					},
				}
			);

			if (stripeError) {
				setError(stripeError.message || "Erreur lors du paiement");
				setLoading(false);
				return;
			}

			if (paymentIntent.status === "succeeded") {
				onSuccess();
			} else {
				setError("Le paiement n'a pas pu être confirmé");
			}
		} catch (err) {
			console.error("Payment error:", err);
			setError(err.message || "Une erreur est survenue lors du paiement");
		} finally {
			setLoading(false);
		}
	};

	const getPlanColor = (idAbonnementType) => {
		const colors = {
			1: "#9E9E9E",
			2: "#4CAF50",
			3: "#2196F3",
			4: "#FF9800",
		};
		return colors[idAbonnementType] || "#30a49a";
	};

	const planColor = getPlanColor(selectedPlan?.idAbonnementType);

	return (
		<form onSubmit={handleSubmit} className="stripe-payment-form">
			{/* Plan Summary */}
			<div className="stripe-plan-summary" style={{ borderLeftColor: planColor }}>
				<h3 className="stripe-plan-name">{selectedPlan?.libelle}</h3>
				<div className="stripe-plan-price" style={{ color: planColor }}>
					{selectedPlan?.prix}€/mois
				</div>
				<ul className="stripe-plan-details">
					<li>{selectedPlan?.maxEleve} élèves maximum</li>
					<li>Abonnement mensuel renouvelé automatiquement</li>
					{selectedPlan?.dureeMois > 0 && (
						<li>Engagement de {selectedPlan?.dureeMois} mois</li>
					)}
					<li>Synchronisation cloud illimitée</li>
				</ul>
			</div>

			{/* Legal Information */}
			<div className="stripe-legal-warning">
				<p><strong>Informations légales</strong></p>
				<ul>
					<li>Abonnement à renouvellement automatique</li>
					{selectedPlan?.dureeMois > 0 && (
						<li>Engagement de {selectedPlan?.dureeMois} mois</li>
					)}
					<li>Résiliable après la période d'engagement</li>
					<li>Total à payer aujourd'hui : {selectedPlan?.prix}€ TTC</li>
				</ul>
			</div>

			{/* Consent Checkboxes */}
			<div className="stripe-consent-section">
				<h4>Consentements requis</h4>

				<label className="stripe-checkbox-row">
					<input
						type="checkbox"
						checked={acceptedCGU}
						onChange={(e) => setAcceptedCGU(e.target.checked)}
					/>
					<span>J'ai lu et j'accepte les Conditions Générales d'Utilisation *</span>
				</label>

				<label className="stripe-checkbox-row">
					<input
						type="checkbox"
						checked={acceptedPrivacy}
						onChange={(e) => setAcceptedPrivacy(e.target.checked)}
					/>
					<span>J'ai lu et j'accepte la Politique de Confidentialité *</span>
				</label>

				<label className="stripe-checkbox-row">
					<input
						type="checkbox"
						checked={acceptedAutoRenewal}
						onChange={(e) => setAcceptedAutoRenewal(e.target.checked)}
					/>
					<span>Je comprends que mon abonnement sera renouvelé automatiquement chaque mois *</span>
				</label>

				<label className="stripe-checkbox-row">
					<input
						type="checkbox"
						checked={acceptedWithdrawalWaiver}
						onChange={(e) => setAcceptedWithdrawalWaiver(e.target.checked)}
					/>
					<span>J'accepte expressément que le service commence immédiatement et je renonce à mon droit de rétractation de 14 jours *</span>
				</label>

				<p className="stripe-required-note">* Champs obligatoires</p>
			</div>

			{/* Card Details */}
			<div className="stripe-card-section">
				<h4>Informations de carte</h4>

				<div className="stripe-input-group">
					<label htmlFor="cardName">Nom sur la carte</label>
					<input
						type="text"
						id="cardName"
						className="stripe-input"
						placeholder="John Doe"
						value={cardName}
						onChange={(e) => setCardName(e.target.value)}
						disabled={loading}
					/>
				</div>

				<div className="stripe-input-group">
					<label>Informations de carte bancaire</label>
					<div className="stripe-card-element-container">
						<CardElement options={CARD_ELEMENT_OPTIONS} />
					</div>
				</div>
			</div>

			{/* Error Message */}
			{error && (
				<div className="stripe-error-message">
					{error}
				</div>
			)}

			{/* Submit Button */}
			<button
				type="submit"
				className="stripe-pay-button"
				style={{ backgroundColor: planColor }}
				disabled={!stripe || loading || !isFormValid()}
			>
				{loading ? (
					<span className="stripe-loading">Traitement en cours...</span>
				) : (
					<span>Payer {selectedPlan?.prix}€</span>
				)}
			</button>

			{/* Security Note */}
			<div className="stripe-security-note">
				<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
					<rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
					<path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
				</svg>
				<span>Paiement sécurisé par Stripe</span>
			</div>
		</form>
	);
}

// Main Modal Component
function StripePaymentModal({ isOpen, onClose, selectedPlan, onPaymentSuccess }) {
	if (!isOpen || !selectedPlan) return null;

	const handleSuccess = () => {
		onPaymentSuccess();
		onClose();
	};

	const handleOverlayClick = (e) => {
		if (e.target === e.currentTarget) {
			onClose();
		}
	};

	return (
		<div className="stripe-modal-overlay" onClick={handleOverlayClick}>
			<div className="stripe-modal-container">
				{/* Header */}
				<div className="stripe-modal-header">
					<h2>Paiement sécurisé</h2>
					<button className="stripe-close-button" onClick={onClose}>
						<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
							<line x1="18" y1="6" x2="6" y2="18"></line>
							<line x1="6" y1="6" x2="18" y2="18"></line>
						</svg>
					</button>
				</div>

				{/* Content */}
				<div className="stripe-modal-content">
					<Elements stripe={stripePromise}>
						<PaymentForm
							selectedPlan={selectedPlan}
							onSuccess={handleSuccess}
							onClose={onClose}
						/>
					</Elements>
				</div>
			</div>
		</div>
	);
}

export default StripePaymentModal;
